import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {finalize, takeUntil, tap} from 'rxjs/operators';
import {IdBase} from '../interfaces';
import {IsLoadingData} from './is-loading.data';

export abstract class FindData<T extends IdBase<IdType>, IdType = number> extends IsLoadingData<T, IdType> {
  private _item$ = new BehaviorSubject<T | undefined>(undefined);
  private cancelPreviousGetCalls$ = new Subject<void>();

  get item$() {
    return this._item$.asObservable();
  }

  peekItem(): T | undefined {
    return this._item$.value;
  }

  pokeItem(item: T | undefined): void {
    this._item$.next(item);
  }

  resetItem(): void {
    this._item$.next(undefined);
  }

  protected abstract findCall(id: IdType): Observable<T>;

  protected createFindCallObservable(id: IdType): Observable<T> {
    this.cancelPreviousGetCalls$.next();
    this.pokeIsLoading(true);

    return this.findCall(id).pipe(
      takeUntil(this.cancelPreviousGetCalls$),
      tap((item) => this.pokeItem(item)),
      finalize(() => this.pokeIsLoading(false))
    );
  }

  loadItem(id: IdType): void {
    this.createFindCallObservable(id).subscribe({
      error: (err) => {
        console.error('Failed to load item', err);
        this.resetItem();
      }
    });
  }

  reloadItem(): void {
    const currentItem = this.peekItem();
    if (currentItem) {
      this.loadItem(currentItem.id);
    }
  }
}
